import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Trash2, ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import { toast } from "sonner";
import API from "../../lib/api";
import { PERSONA_DEFINITIONS } from "../../config/surveyConfig";

export default function AdminResponses() {
  const navigate = useNavigate();
  const [data, setData] = useState({ responses: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ persona: "", usage_level: "" });
  const [showFilters, setShowFilters] = useState(false);
  const limit = 20;

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (filters.persona) params.persona = filters.persona;
      if (filters.usage_level) params.usage_level = filters.usage_level;
      const { data: res } = await API.get("/admin/responses", { params });
      setData(res);
    } catch {
      toast.error("Failed to load responses");
    }
    setLoading(false);
  }, [page, search, filters]);

  useEffect(() => { fetchResponses(); }, [fetchResponses]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this response?")) return;
    try {
      await API.delete(`/admin/responses/${id}`);
      toast.success("Response deleted");
      fetchResponses();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const totalPages = Math.ceil(data.total / limit);
  const personaDef = PERSONA_DEFINITIONS;

  const clearFilters = () => {
    setFilters({ persona: "", usage_level: "" });
    setSearch("");
    setPage(1);
  };

  const hasActiveFilters = search || filters.persona || filters.usage_level;

  return (
    <div className="space-y-4" data-testid="admin-responses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="font-parkinsans text-2xl sm:text-3xl">Responses</h1>
        <span className="nb-badge bg-[#f5d0fe]" data-testid="response-count">{data.total} total</span>
      </div>

      {/* Search and Filters */}
      <div className="nb-card-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={2.5} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="nb-input pl-10 text-sm"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`nb-btn-secondary text-sm py-2 px-4 ${showFilters ? "bg-[#f5d0fe]" : ""}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" strokeWidth={2.5} /> Filters
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="nb-btn-secondary text-sm py-2 px-4" data-testid="clear-filters-btn">
              <X className="w-4 h-4" strokeWidth={2.5} /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mt-3 pt-3 border-t-2 border-[#e5e5e5] flex flex-wrap gap-3" data-testid="filter-panel">
            <select
              value={filters.persona}
              onChange={(e) => { setFilters(p => ({ ...p, persona: e.target.value })); setPage(1); }}
              className="nb-input text-sm py-2 w-auto"
              data-testid="filter-persona"
            >
              <option value="">All Personas</option>
              {Object.keys(personaDef).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={filters.usage_level}
              onChange={(e) => { setFilters(p => ({ ...p, usage_level: e.target.value })); setPage(1); }}
              className="nb-input text-sm py-2 w-auto"
              data-testid="filter-usage"
            >
              <option value="">All Usage Levels</option>
              {["Regular", "Occasional", "Tried", "Never"].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#a21caf] border-t-transparent rounded-full" />
        </div>
      ) : data.responses.length === 0 ? (
        <div className="nb-card text-center py-12" data-testid="empty-responses">
          <div className="text-4xl mb-3">📭</div>
          <h3 className="font-parkinsans text-xl mb-1">No responses found</h3>
          <p className="text-gray-500 text-sm">
            {hasActiveFilters ? "Try adjusting your filters." : "Responses will appear here once people start the survey."}
          </p>
        </div>
      ) : (
        <div className="nb-card overflow-x-auto !p-0" data-testid="responses-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[#111] bg-[#f5d0fe]/30">
                <th className="text-left p-3 font-bold">Date/Time</th>
                <th className="text-left p-3 font-bold">Name</th>
                <th className="text-left p-3 font-bold hidden md:table-cell">Email</th>
                <th className="text-left p-3 font-bold">Status</th>
                <th className="text-left p-3 font-bold">Persona</th>
                <th className="text-left p-3 font-bold">Score</th>
                <th className="text-right p-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.responses.map((r) => (
                <tr key={r.id} className={`border-b border-gray-200 hover:bg-[#fdf7ff] transition-colors ${r.status === 'in_progress' ? 'bg-yellow-50/50' : ''}`} data-testid={`response-row-${r.id}`}>
                  <td className="p-3 text-gray-500 text-xs whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString('en-US', { 
                      month: 'numeric', 
                      day: 'numeric', 
                      year: '2-digit',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="p-3 font-medium">{r.short_name}</td>
                  <td className="p-3 text-gray-500 hidden md:table-cell text-xs">{r.email}</td>
                  <td className="p-3">
                    {r.status === 'in_progress' ? (
                      <span className="nb-badge text-xs bg-yellow-100 border-yellow-400 text-yellow-700">
                        In Progress
                      </span>
                    ) : (
                      <span className="nb-badge text-xs bg-green-100 border-green-400 text-green-700">
                        Completed
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {r.persona ? (
                      <span className="nb-badge text-xs bg-[#f5d0fe]" style={{ borderColor: personaDef[r.persona]?.color || "#111" }}>
                        {personaDef[r.persona]?.emoji} {r.persona}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="p-3 font-bold text-[#a21caf]">
                    {r.status === 'in_progress' ? '-' : `${r.total_score}/36`}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      {r.status === 'completed' && (
                        <button
                          onClick={() => navigate(`/admin/responses/${r.id}`)}
                          className="p-2 rounded-lg border-2 border-[#111] bg-white hover:bg-[#f5d0fe] transition-colors shadow-[2px_2px_0px_0px_rgba(17,17,17,1)] hover:shadow-[3px_3px_0px_0px_rgba(17,17,17,1)] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
                          data-testid={`view-btn-${r.id}`}
                        >
                          <Eye className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-2 rounded-lg border-2 border-[#111] bg-white hover:bg-red-50 text-red-500 transition-colors shadow-[2px_2px_0px_0px_rgba(17,17,17,1)] hover:shadow-[3px_3px_0px_0px_rgba(17,17,17,1)] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
                        data-testid={`delete-btn-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3" data-testid="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`nb-btn-secondary text-sm py-2 px-3 ${page === 1 ? "opacity-40" : ""}`}
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <span className="text-sm font-semibold">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`nb-btn-secondary text-sm py-2 px-3 ${page === totalPages ? "opacity-40" : ""}`}
            data-testid="next-page-btn"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}
